import React, { CSSProperties, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

import { ArrowRightOutlined } from '@ant-design/icons';
import { Card, Result, Skeleton, Space, Typography } from 'antd';

import { UserContext } from '../contexts/UserContext';
import { useGetClientOrganizationDetailsQuery } from '../graphql/generated';

const pageStyle: CSSProperties = {
  margin: '0 auto',
  maxWidth: 1200,
};

const welcomeCardStyle: CSSProperties = {
  background: 'linear-gradient(90deg, #fdf4f9 0%, #f2ecfa 100%)',
  borderRadius: 12,
  marginBottom: 24,
  overflow: 'hidden',
};

const guideRailStyle: CSSProperties = {
  background: '#fdfafc',
  border: '1px solid #efe3ea',
  borderRadius: 12,
  padding: 24,
};

const guideLabelStyle: CSSProperties = {
  color: '#870051',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: 1.2,
};

const stepRowStyle: CSSProperties = {
  borderTop: '1px solid #f0e6ec',
  display: 'flex',
  gap: 12,
  padding: '12px 0',
};

const stepNumberStyle: CSSProperties = {
  alignItems: 'center',
  background: '#870051',
  borderRadius: 12,
  color: '#ffffff',
  display: 'flex',
  flex: '0 0 auto',
  fontSize: 13,
  fontWeight: 600,
  height: 24,
  justifyContent: 'center',
  width: 24,
};

const getOnboardingSteps = (
  organizationName: string
): {
  description: string;
  href?: string;
  linkText?: string;
  path?: string;
  title: string;
}[] => [
  {
    description: 'Add the CMS participants list file for your network.',
    linkText: 'File Uploads',
    path: '/file-uploads',
    title: 'Upload your participants list',
  },
  {
    description:
      'Review the practices found in your list and approve their creation.',
    title: 'Approve practice creation',
  },
  {
    description:
      'Update practice names, add addresses, logos, and language preferences',
    linkText: 'Practices',
    path: '/client-practices',
    title: 'Complete practice details',
  },
  {
    description:
      'Get the SVA packets for your network from the Forms page and distribute to practices.',
    linkText: 'Forms',
    path: '/template-previews',
    title: 'Download SVA packets',
  },
  {
    description: 'Let the Mabel team know once your setup is complete.',
    href: `mailto:support@getmabel.com?subject=${encodeURIComponent(
      `Network setup complete — ${organizationName}`
    )}&body=${encodeURIComponent(
      `Hi Mabel team,\n\nWe have completed the setup steps for ${organizationName}.\n\nThanks!`
    )}`,
    linkText: 'Email support@getmabel.com',
    title: 'Notify the Mabel team',
  },
];

const ClientHomePage: React.FC = () => {
  const { user } = useContext(UserContext);
  const { data, loading, error } = useGetClientOrganizationDetailsQuery({
    variables: { user_id: user?.user_id || '' },
    skip: !user?.user_id,
  });
  const organization = data?.users_by_pk?.client_organization;
  const firstName = user?.claims?.name?.trim().split(/\s+/)[0];
  const activeAcos =
    organization?.organization_networks.filter(
      ({ is_archived, network }) => !is_archived && !network.is_archived
    ).length || 0;

  if (loading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (error)
    return (
      <Result
        status="error"
        title="There was an error retrieving your organization"
        subTitle={error.message}
      />
    );
  if (!organization)
    return (
      <Result
        status="warning"
        title="No organization profile found"
        subTitle="Your account is not attached to a client organization."
      />
    );

  return (
    <>
      <Helmet>
        <title>Home | Mabel</title>
      </Helmet>
      <main style={pageStyle}>
        <Card
          bordered={false}
          bodyStyle={{ padding: '32px 40px' }}
          style={welcomeCardStyle}
        >
          <Typography.Text
            style={{
              color: '#870051',
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: 0.6,
            }}
          >
            {organization.organization_name.toUpperCase()}
          </Typography.Text>
          <Typography.Title level={1} style={{ marginBottom: 8, marginTop: 8 }}>
            Welcome{firstName ? `, ${firstName}` : ''}
          </Typography.Title>
          <Typography.Paragraph
            style={{ fontSize: 17, marginBottom: 24, maxWidth: 720 }}
            type="secondary"
          >
            {organization.organization_description ||
              'Manage your organization, review outreach materials, and explore your data from one place.'}
          </Typography.Paragraph>
          <Space size="large" wrap>
            <Typography.Text strong>
              {activeAcos} active{' '}
              {activeAcos === 1 ? 'ACO entity' : 'ACO entities'}
            </Typography.Text>
            <Link to="/aco-entities">
              View organization profile <ArrowRightOutlined />
            </Link>
          </Space>
        </Card>

        <section aria-label="Getting started" style={guideRailStyle}>
          <Typography.Text style={guideLabelStyle}>
            GETTING STARTED
          </Typography.Text>
          <Typography.Title
            level={4}
            style={{ marginBottom: 16, marginTop: 4 }}
          >
            Set up your network in five steps
          </Typography.Title>
          {getOnboardingSteps(organization.organization_name).map(
            (step, index) => (
              <div key={step.title} style={stepRowStyle}>
                <div style={stepNumberStyle}>{index + 1}</div>
                <div>
                  <Typography.Text strong>{step.title}</Typography.Text>
                  <Typography.Paragraph
                    style={{ fontSize: 13, marginBottom: 4, marginTop: 2 }}
                    type="secondary"
                  >
                    {step.description}
                  </Typography.Paragraph>
                  {step.path && (
                    <Link style={{ fontSize: 13 }} to={step.path}>
                      {step.linkText} <ArrowRightOutlined />
                    </Link>
                  )}
                  {step.href && (
                    <a href={step.href} style={{ fontSize: 13 }}>
                      {step.linkText} <ArrowRightOutlined />
                    </a>
                  )}
                </div>
              </div>
            )
          )}
        </section>
      </main>
    </>
  );
};

export default ClientHomePage;
